/**
 * BRISTM — shared behaviour for bristm-* sections.
 *
 * Every feature is attribute-driven and idempotent so it can re-run when the
 * theme editor reloads a section (shopify:section:load).
 *
 *  - Theme toggle ........ [data-b-theme-set="light|dark"]
 *  - Scroll reveal ....... [data-b-reveal] (optional style="--b-reveal-delay:.1s")
 *  - Count-up numbers .... [data-b-count="250"] (text content is replaced)
 *  - Accordions .......... [data-b-accordion] > [data-b-acc-item] > [data-b-acc-trigger] + [data-b-acc-panel]
 *                          add data-b-accordion="single" to keep one item open
 *  - Header mega menu .... [data-b-header]
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'bristm-theme';
  var root = document.documentElement;
  root.classList.add('b-js');

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- Theme ---------------- */
  function applyTheme(theme) {
    root.setAttribute('data-theme', theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch (e) {}
    document.querySelectorAll('[data-b-theme-set]').forEach(function (btn) {
      btn.setAttribute('aria-pressed', String(btn.getAttribute('data-b-theme-set') === theme));
    });
    document.dispatchEvent(new CustomEvent('bristm:theme', { detail: { theme: theme } }));
  }

  function initTheme(scope) {
    scope.querySelectorAll('[data-b-theme-set]').forEach(function (btn) {
      btn.setAttribute('aria-pressed', String(btn.getAttribute('data-b-theme-set') === root.getAttribute('data-theme')));
      if (btn.dataset.bBound) return;
      btn.dataset.bBound = '1';
      btn.addEventListener('click', function () {
        applyTheme(btn.getAttribute('data-b-theme-set'));
      });
    });
  }

  /* ---------------- Reveal ---------------- */
  var revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                entry.target.classList.add('is-revealed');
                revealObserver.unobserve(entry.target);
              }
            });
          },
          { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
        )
      : null;

  function initReveal(scope) {
    scope.querySelectorAll('[data-b-reveal]:not(.is-revealed)').forEach(function (el) {
      if (!revealObserver || reduceMotion) {
        el.classList.add('is-revealed');
      } else {
        revealObserver.observe(el);
      }
    });
  }

  /* ---------------- Count-up ---------------- */
  function animateCount(el) {
    var target = parseFloat(el.getAttribute('data-b-count'));
    if (isNaN(target)) return;
    var decimals = (el.getAttribute('data-b-count').split('.')[1] || '').length;
    if (reduceMotion) {
      el.textContent = target.toFixed(decimals);
      return;
    }
    var duration = 1600;
    var start = null;
    function step(ts) {
      if (!start) start = ts;
      var p = Math.min((ts - start) / duration, 1);
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = (target * eased).toFixed(decimals);
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  var countObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                animateCount(entry.target);
                countObserver.unobserve(entry.target);
              }
            });
          },
          { threshold: 0.4 }
        )
      : null;

  function initCounters(scope) {
    scope.querySelectorAll('[data-b-count]').forEach(function (el) {
      if (el.dataset.bBound) return;
      el.dataset.bBound = '1';
      if (countObserver && !reduceMotion) {
        el.textContent = '0';
        countObserver.observe(el);
      } else {
        animateCount(el);
      }
    });
  }

  /* ---------------- Accordion ---------------- */
  function setItem(item, open) {
    item.classList.toggle('is-open', open);
    var trigger = item.querySelector('[data-b-acc-trigger]');
    if (trigger) trigger.setAttribute('aria-expanded', String(open));
  }

  function initAccordions(scope) {
    scope.querySelectorAll('[data-b-accordion]').forEach(function (acc) {
      if (acc.dataset.bBound) return;
      acc.dataset.bBound = '1';
      var single = acc.getAttribute('data-b-accordion') === 'single';
      var items = Array.prototype.slice.call(acc.querySelectorAll('[data-b-acc-item]'));
      items.forEach(function (item) {
        var trigger = item.querySelector('[data-b-acc-trigger]');
        if (!trigger) return;
        setItem(item, item.classList.contains('is-open'));
        trigger.addEventListener('click', function () {
          var willOpen = !item.classList.contains('is-open');
          if (single) {
            items.forEach(function (other) {
              if (other !== item) setItem(other, false);
            });
          }
          setItem(item, willOpen);
        });
      });
    });
  }

  /* ---------------- Header ---------------- */
  function initHeader(scope) {
    var header = scope.querySelector('[data-b-header]');
    if (!header || header.dataset.bBound) return;
    header.dataset.bBound = '1';

    var items = Array.prototype.slice.call(header.querySelectorAll('[data-b-nav-item]'));
    var closeTimer = null;
    var desktop = window.matchMedia('(min-width: 1200px)');

    function openItem(item) {
      clearTimeout(closeTimer);
      items.forEach(function (other) {
        if (other !== item) closeItem(other);
      });
      item.classList.add('is-open');
      header.classList.add('has-open-mega');
      var t = item.querySelector('[data-b-nav-trigger]');
      if (t) t.setAttribute('aria-expanded', 'true');
    }

    function closeItem(item) {
      item.classList.remove('is-open');
      var t = item.querySelector('[data-b-nav-trigger]');
      if (t) t.setAttribute('aria-expanded', 'false');
      if (!header.querySelector('[data-b-nav-item].is-open')) header.classList.remove('has-open-mega');
    }

    function closeAll() {
      items.forEach(closeItem);
    }

    items.forEach(function (item) {
      var trigger = item.querySelector('[data-b-nav-trigger]');
      if (!trigger) return;

      item.addEventListener('mouseenter', function () {
        if (desktop.matches) openItem(item);
      });
      item.addEventListener('mouseleave', function () {
        if (!desktop.matches) return;
        closeTimer = setTimeout(function () {
          closeItem(item);
        }, 180);
      });
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        if (item.classList.contains('is-open')) closeItem(item);
        else openItem(item);
      });
      item.addEventListener('focusout', function (e) {
        if (!item.contains(e.relatedTarget)) closeItem(item);
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var open = header.querySelector('[data-b-nav-item].is-open');
      if (open) {
        closeItem(open);
        var t = open.querySelector('[data-b-nav-trigger]');
        if (t) t.focus();
      }
      if (header.classList.contains('is-drawer-open')) toggleDrawer(false);
    });

    document.addEventListener('click', function (e) {
      if (!header.contains(e.target)) closeAll();
    });

    /* Scrolled state */
    function onScroll() {
      header.classList.toggle('is-scrolled', window.scrollY > 10);
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    /* Mobile drawer */
    var burger = header.querySelector('[data-b-drawer-toggle]');
    var drawer = header.querySelector('[data-b-drawer]');
    function toggleDrawer(force) {
      var open = typeof force === 'boolean' ? force : !header.classList.contains('is-drawer-open');
      header.classList.toggle('is-drawer-open', open);
      if (burger) burger.setAttribute('aria-expanded', String(open));
      if (drawer) drawer.setAttribute('aria-hidden', String(!open));
      document.documentElement.classList.toggle('b-no-scroll', open);
    }
    if (burger) {
      burger.addEventListener('click', function () {
        toggleDrawer();
      });
    }
    desktop.addEventListener('change', function () {
      toggleDrawer(false);
      closeAll();
    });
  }

  /* ---------------- Sticky search bar ----------------
   * Hero search pills ([data-b-sticky-search]) are moved into a body-level
   * container so they can stay fixed at the bottom of the viewport regardless
   * of the hero's stacking context. Hidden while the footer is on screen or
   * the mobile drawer is open. */
  var stickyRoot = null;
  var footerObserver = null;

  function getStickyRoot() {
    if (stickyRoot && document.body.contains(stickyRoot)) return stickyRoot;
    stickyRoot = document.createElement('div');
    stickyRoot.className = 'b-sticky-root b-scope is-away';
    document.body.appendChild(stickyRoot);

    var footer = document.querySelector('body > .page-wrapper > footer, body > footer, footer');
    if (footer && 'IntersectionObserver' in window) {
      footerObserver = new IntersectionObserver(
        function (entries) {
          stickyRoot.classList.toggle('is-footer', entries[0].isIntersecting);
        },
        { threshold: 0 }
      );
      footerObserver.observe(footer);
    }
    return stickyRoot;
  }

  function initStickySearch(scope) {
    var forms = scope.querySelectorAll('[data-b-sticky-search]');
    if (!forms.length) return;
    var rootEl = getStickyRoot();

    forms.forEach(function (form) {
      if (form.parentElement === rootEl) return;
      var section = form.closest('.shopify-section');
      var sectionId = section ? section.id : '';
      form.setAttribute('data-b-section', sectionId);

      // Replace a stale copy from the same section (theme editor reloads)
      rootEl.querySelectorAll('[data-b-sticky-search]').forEach(function (old) {
        if (old.getAttribute('data-b-section') === sectionId) old.remove();
      });

      // Only one sticky bar per page: the first hero wins
      if (rootEl.querySelector('[data-b-sticky-search]')) {
        form.remove();
        return;
      }
      rootEl.appendChild(form);
    });

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        rootEl.classList.remove('is-away');
      });
    });
  }

  /* Theme editor: selecting a menu block opens its mega panel */
  document.addEventListener('shopify:block:select', function (e) {
    var item = e.target.closest && e.target.closest('[data-b-nav-item]');
    if (!item) return;
    var header = item.closest('[data-b-header]');
    header.querySelectorAll('[data-b-nav-item].is-open').forEach(function (other) {
      if (other !== item) other.classList.remove('is-open');
    });
    item.classList.add('is-open');
    header.classList.add('has-open-mega');
  });

  document.addEventListener('shopify:block:deselect', function (e) {
    var item = e.target.closest && e.target.closest('[data-b-nav-item]');
    if (!item) return;
    item.classList.remove('is-open');
    item.closest('[data-b-header]').classList.remove('has-open-mega');
  });

  document.addEventListener('shopify:section:unload', function (e) {
    if (!stickyRoot) return;
    stickyRoot.querySelectorAll('[data-b-section="' + e.target.id + '"]').forEach(function (form) {
      form.remove();
    });
  });

  /* ---------------- Boot ---------------- */
  function init(scope) {
    scope = scope || document;
    initTheme(scope);
    initHeader(scope);
    initAccordions(scope);
    initCounters(scope);
    initReveal(scope);
    initStickySearch(scope);
    document.dispatchEvent(new CustomEvent('bristm:init', { detail: { scope: scope } }));
  }

  window.BRISTM = { init: init, applyTheme: applyTheme };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
